import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Link, useNavigate } from 'react-router';
import { clearNextAuthRedirect, getNextAuthRedirect, useAuth } from '@/contexts/auth-context';

import { ArcanaLogo } from '@/components/custom/arcana-logo';
import { PasswordInput } from '@/components/custom/password-input';
import { ThemeToggle } from '@/components/custom/theme-toggle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowRight, Loader2, ShieldAlert, ShieldCheck } from 'lucide-react';
import { ADMIN_DASHBOARD_PATH, MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH, TENANT_DASHBOARD_PATH } from '@/config/constants';

type SignInFormProps = {
  userRole: 'admin' | 'tenant';
  title?: string;
  description?: string;
  redirectPath: string;
};

const signInSchema = z.object({
  email: z
    .email()
    .nonempty('Email is required'),
  password: z
    .string()
    .nonempty('Password is required')
    .min(MIN_PASSWORD_LENGTH, `Password must be at least ${MIN_PASSWORD_LENGTH} characters`)
    .max(MAX_PASSWORD_LENGTH, `Password must be at most ${MAX_PASSWORD_LENGTH} characters`),
});

export function AdminSignIn() {
  return <SignInForm
    userRole="admin"
    redirectPath={ADMIN_DASHBOARD_PATH}
    title="Admin Sign In"
    description="Enter your credentials to access your admin account"
  />;
}

export function TenantSignIn() {
  return <SignInForm
    userRole="tenant"
    redirectPath={TENANT_DASHBOARD_PATH}
    title="Tenant Sign In"
    description="Enter your credentials to access your tenant account"
  />;
}

type SignInFormValues = z.infer<typeof signInSchema>;

function SignInForm({
  userRole,
  title = 'Sign In',
  description = 'Enter your credentials to access your account',
  redirectPath,
}: SignInFormProps) {
  const navigate = useNavigate();
  const { authenticate } = useAuth();
  const [error, setError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const form = useForm<SignInFormValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onTouched',
  });

  const onSubmit = async (values: SignInFormValues) => {
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await authenticate(userRole, values.email, values.password);
      if (!result) {
        setError('Invalid credentials');
        return;
      }
      const nextRedirect = getNextAuthRedirect();
      if (nextRedirect) {
        clearNextAuthRedirect();
        navigate(nextRedirect);
        return;
      }
      navigate(redirectPath);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-background text-foreground">
      <div className="absolute right-4 top-4 z-20 sm:right-6 sm:top-6">
        <ThemeToggle className="border border-border bg-card/80 text-foreground shadow-sm backdrop-blur-sm hover:bg-card" />
      </div>

      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[-12%] top-[-24%] h-[620px] w-[620px] rounded-full bg-primary/20 blur-3xl dark:bg-primary/25" />
        <div className="absolute right-[-14%] bottom-[-20%] h-[560px] w-[560px] rounded-full bg-secondary/30 blur-3xl dark:bg-secondary/20" />
        <div className="absolute inset-0 bg-linear-to-br from-background/40 via-background/70 to-background/90" />
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center">
        <section className="flex items-center justify-center w-11/12 md:w-xl sm:p-10">
          <div className="w-full rounded-3xl border border-border bg-card/90 p-7 shadow-2xl shadow-primary/10 backdrop-blur-sm sm:p-8">
            <div className="mb-6 space-y-2">
              <div className="mb-3">
                <ArcanaLogo variant="compact" className="opacity-95" />
              </div>
              <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                <ShieldCheck className="size-3.5" />
                Editorial CMS
              </div>
              <h2 className="font-display text-3xl font-semibold text-foreground">
                {title}
              </h2>
              <p className="text-sm text-muted-foreground">{description}</p>
            </div>

            {error && (
              <Alert className="mb-4 border-destructive/40 bg-destructive/10 text-destructive shadow-sm">
                <ShieldAlert className="size-4" />
                <AlertDescription className="text-sm font-medium text-destructive/90">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                        {userRole} email
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="name@arcana-cms.com"
                          className="h-11 rounded-xl border-border bg-background"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <div className="mb-2 flex items-center justify-between">
                        <FormLabel className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                          Password
                        </FormLabel>
                        {/*<span className="text-xs text-primary">Forgot access?</span>*/}
                      </div>
                      <FormControl>
                        <PasswordInput
                          placeholder="Enter your password"
                          className="h-11 rounded-xl border-border bg-background pr-10"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="mt-2 h-11 w-full rounded-xl font-semibold"
                  disabled={isSubmitting || !form.formState.isValid}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Login to Console
                      <ArrowRight className="ml-2 size-4" />
                    </>
                  )}
                </Button>
              </form>
            </Form>

            <div className="mt-6 border-t border-border/70 pt-4 text-center text-xs text-muted-foreground">
              <div className="flex items-center justify-center gap-3">
                <span>Privacy</span>
                <span className="text-muted-foreground/60">•</span>
                <span>Security</span>
                <span className="text-muted-foreground/60">•</span>
                <span>Support</span>
              </div>
              <p className="mt-2">Protected by Arcana CMS authentication controls.</p>
            </div>

            <div className="mt-4 text-center text-xs text-muted-foreground lg:hidden">
              <Link to="/" className="inline-flex items-center gap-1 text-primary hover:underline">
                Back to home
              </Link>
            </div>
          </div>
          <p className="font-display text-6xl font-semibold tracking-tight text-foreground/10 xl:text-7xl absolute bottom-10 left-10">
            ARCANA
          </p>
        </section>
      </div>
    </div>
  );
}
